import { useState, useEffect, useCallback } from 'react';
import Typography from '../StyledTypography';
import { useTranslation } from 'react-i18next';

import styles from './RequestAuditTrails.module.css';
import DataTableComponent from '../DataTable/PrimeDataTable';
import axiosInstance from '../../../axiosInstance';
import { convertUTCToLocal } from '../../utils/dateUtils';
import { useFileDownload } from '../../../hooks/api/files/useFileDownload';
import { toast } from 'react-toastify';

const RequestAuditTrails = ({ requestId }) => {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const { downloadFile, error } = useFileDownload();

  const fetchAuditTrails = useCallback(async () => {
    try {
      if (!requestId) {
        return;
      }
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/v1/request-audit-trails/request/${requestId}`
      );
      setData(response.data.data);
    } catch (error) {
      console.log('Error fetching audit trails:', error);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchAuditTrails();
  }, [fetchAuditTrails]);

  const columns = [
    {
      field: 'actor.username',
      header: 'Username',
      sortable: true,
      filter: true,
    },
    {
      field: 'actor.email',
      header: 'Email',
      sortable: true,
      filter: true,
    },
    {
      field: 'action',
      header: 'Action',
      sortable: true,
      filter: true,
    },
    {
      field: 'entity.comment',
      header: 'Comment',
      sortable: true,
      filter: true,
    },
    {
      field: 'createdAt',
      header: 'Created at',
      sortable: true,
      filter: true,
      body: (rowData) => convertUTCToLocal(rowData.createdAt),
    },
  ];

  const actions = [
    {
      text: t('downloadAttachment'),
      color: 'primary',
      onClick: async (row) => {
        if (row?.attachments && row?.attachments?.length > 0) {
          try {
            // Downloads only single file
            await downloadFile(
              row?.attachments[0]?.file?.id,
              row?.attachments[0]?.file?.original_name
            );

            if (error) {
              toast.error(error, { position: 'top-center' });
            }
          } catch (error) {
            console.error('Error downloading file:', error);
          }
        }
      },
    },
  ];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Typography variant="bodyDefault" color="textSecondary">
          {t('loading')}
        </Typography>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Typography variant="bodyDefault" color="textSecondary">
          {t('noAuditTrailsFound')}
        </Typography>
      </div>
    );
  }

  return (
    <div>
      <DataTableComponent
        data={data}
        columns={columns}
        actions={actions}
        actionsHeader={t('attachments')}
        enablePagination={true}
        enableLazyLoading={false}
        enableExport={true}
      />
    </div>
  );
};

export default RequestAuditTrails;
